import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskMode,
  TaskType,
} from '@heygen/streaming-avatar';
import {
  useLivekitAvatar,
  type AvatarConfig,
  type AvatarCallbacks,
} from '@/hooks/useLivekitAvatar';

export type VideoEngine = 'heygen' | 'livekit';
export type VideoConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface VideoEngineConfig {
  // HeyGen-specific options
  avatarId?: string; // Made optional since LiveKit doesn't use it
  voiceId?: string; // Made optional since LiveKit has different voice handling
  instructions?: string;
  quality?: 'low' | 'medium' | 'high';

  // Common options
  firstMessage?: string;
  language?: string; // Language for both HeyGen and LiveKit

  // LiveKit-specific options
  roomName?: string;
  participantName?: string;
  avatarImage?: string;
}

export interface VideoEngineCallbacks {
  onConnect?: () => void;
  onError?: (error: any) => void;
  onMessage?: (data: { source: 'ai' | 'user'; message: string }) => void;
  onDebug?: (data: any) => void;
  onStatusChange?: (status: VideoConnectionStatus) => void;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onAgentSpeaking?: (isSpeaking: boolean) => void;
  onSessionEnd?: () => void;
}

export interface VideoEngineHook {
  // Core functions
  start: (
    config: VideoEngineConfig,
    callbacks?: VideoEngineCallbacks,
    preferredEngine?: VideoEngine,
  ) => Promise<string>;
  stop: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  sendMessage: (message: string) => void;

  // State
  status: VideoConnectionStatus;
  engine: VideoEngine;
  isRecording: boolean;
  isSpeaking: boolean;
  isPaused: boolean;
  error: Error | null;
  sessionId: string | null;

  // UI Refs
  videoRef: React.RefObject<HTMLDivElement>;
  userVideoRef: React.RefObject<HTMLVideoElement>;

  // Utilities
  getCurrentEngine: () => VideoEngine;
}

// Helper function to generate HeyGen session token
export async function generateHeyGenSessionToken(
  apiKey: string,
): Promise<string> {
  const createTokenUrl = 'https://api.heygen.com/v1/streaming.create_token';

  try {
    const response = await fetch(createTokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to generate token: ${response.status} - ${errorText}`,
      );
    }

    const data = await response.json();

    if (!data.data?.token) {
      throw new Error('No token in response');
    }

    return data.data.token;
  } catch (error) {
    console.error('[generateHeyGenSessionToken] Error:', error);
    throw error;
  }
}

export function useVideoEngine(preferredEngine?: VideoEngine): VideoEngineHook {
  // Stable engine selection to prevent re-renders
  const engine: VideoEngine = useMemo(() => {
    // If preferredEngine is provided, use it
    if (preferredEngine) {
      return preferredEngine;
    }

    // Default to HeyGen
    return 'heygen';
  }, [preferredEngine]);

  // Track engine changes and only log when it actually changes
  const previousEngineRef = useRef<VideoEngine | null>(null);
  const engineLoggedRef = useRef(false);

  if (!engineLoggedRef.current || previousEngineRef.current !== engine) {
    console.log('🎬 Video Engine initialized:', engine);
    engineLoggedRef.current = true;
    previousEngineRef.current = engine;
  }

  // State
  const [status, setStatus] = useState<VideoConnectionStatus>('disconnected');
  const [isPaused, setPaused] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Refs
  const videoRef = useRef<HTMLDivElement>(null);
  const userVideoRef = useRef<HTMLVideoElement>(null);
  const streamingAvatarRef = useRef<any>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const callbacksRef = useRef<VideoEngineCallbacks>({});
  const currentAvatarMessageRef = useRef<string>('');
  const currentUserMessageRef = useRef<string>('');
  const isAvatarSpeakingRef = useRef<boolean>(false);
  const isUserSpeakingRef = useRef<boolean>(false);
  const avatarMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep current cleanup function in ref for stable access
  const cleanupRef = useRef<(() => Promise<void>) | null>(null);

  // Stable callbacks to prevent re-initialization - remove engine dependency
  const liveKitAvatarCallbacks = useMemo(() => ({
    onConnectionChange: (isConnected: boolean) => {
      const newStatus = isConnected ? 'connected' : 'disconnected';
      setStatus(newStatus);
      callbacksRef.current.onStatusChange?.(newStatus);

      if (isConnected) {
        callbacksRef.current.onConnect?.();
      } else {
        callbacksRef.current.onSessionEnd?.();
      }
    },
    onAgentSpeaking: (speaking: boolean) => {
      setIsSpeaking(speaking);
      callbacksRef.current.onAgentSpeaking?.(speaking);
    },
  }), []); // Empty dependency array for maximum stability

  // Initialize LiveKit Avatar with stable callbacks
  const liveKitAvatar = useLivekitAvatar(liveKitAvatarCallbacks);

  // Connect LiveKit avatar to the external video container
  useEffect(() => {
    if (engine === 'livekit') {
      liveKitAvatar.attachToVideoContainer(videoRef);
    }
  }, [engine, liveKitAvatar]);

  // Cleanup function
  const cleanup = useCallback(async () => {
    console.log(`[VideoEngine] Starting cleanup for ${engine} engine`);

    // Clear timeouts
    if (avatarMessageTimeoutRef.current) {
      clearTimeout(avatarMessageTimeoutRef.current);
      avatarMessageTimeoutRef.current = null;
    }

    if (userMessageTimeoutRef.current) {
      clearTimeout(userMessageTimeoutRef.current);
      userMessageTimeoutRef.current = null;
    }

    if (engine === 'heygen') {
      // Stop HeyGen streaming avatar
      if (streamingAvatarRef.current) {
        try {
          // Check if methods exist before calling
          if (typeof streamingAvatarRef.current.stopVoiceChat === 'function') {
            await streamingAvatarRef.current.stopVoiceChat();
          }
          if (typeof streamingAvatarRef.current.terminate === 'function') {
            await streamingAvatarRef.current.terminate();
          } else if (typeof streamingAvatarRef.current.destroy === 'function') {
            await streamingAvatarRef.current.destroy();
          }
        } catch (e) {
          console.error('[VideoEngine] Error during HeyGen cleanup:', e);
        }
        streamingAvatarRef.current = null;
      }

      // Clear video element for HeyGen
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = null;
        // Remove from DOM if it has a parent
        if (videoElementRef.current.parentNode) {
          videoElementRef.current.parentNode.removeChild(
            videoElementRef.current,
          );
        }
        videoElementRef.current = null;
      }
    } else if (engine === 'livekit') {
      // Stop LiveKit avatar
      try {
        await liveKitAvatar.stopAvatarSession();
      } catch (e) {
        console.error('[VideoEngine] Error during LiveKit cleanup:', e);
      }
    }

    // Reset state
    setStatus('disconnected');
    setIsRecording(false);
    setIsSpeaking(false);
    setPaused(false);
    setError(null);
    setSessionId(null);

    // Reset refs
    currentAvatarMessageRef.current = '';
    currentUserMessageRef.current = '';
    isAvatarSpeakingRef.current = false;
    isUserSpeakingRef.current = false;

    console.log(`[VideoEngine] Cleanup completed for ${engine} engine`);
  }, [engine, liveKitAvatar]);

  // Update cleanup ref whenever cleanup changes
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  // Start LiveKit Avatar Session
  const startLiveKit = useCallback(
    async (
      config: VideoEngineConfig,
      callbacks: VideoEngineCallbacks,
    ): Promise<string> => {
      // Prevent duplicate starts - check both local status and LiveKit connection state
      if (status === 'connecting' || status === 'connected' ||
          liveKitAvatar.isConnecting || liveKitAvatar.isConnected) {
        console.log('[VideoEngine] LiveKit session already starting/started, skipping');
        return sessionId || 'existing-session';
      }

      if (!config.roomName || !config.participantName) {
        throw new Error('LiveKit Avatar requires roomName and participantName');
      }

      console.log('[VideoEngine] Starting LiveKit Avatar session:', {
        roomName: config.roomName,
        participantName: config.participantName,
        timestamp: new Date().toISOString(),
      });

      // Set status first to prevent concurrent calls
      setStatus('connecting');

      // Create unique session ID
      const newSessionId = `livekit-video-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 11)}`;
      setSessionId(newSessionId);

      try {
        const avatarConfig: AvatarConfig = {
          firstMessage: config.firstMessage || '',
          voiceId: config.voiceId,
          roomName: config.roomName,
          participantName: config.participantName,
          avatarImage: config.avatarImage,
          avatarId: config.avatarId,
        };

        const avatarCallbacks: AvatarCallbacks = {
          onConnect: () => {
            console.log('[VideoEngine] LiveKit Avatar connected');
            setStatus('connected');
            callbacks.onConnect?.();
            callbacks.onStatusChange?.('connected');
          },
          onError: (error) => {
            console.error('[VideoEngine] LiveKit Avatar error:', error);
            const errorObj =
              error instanceof Error ? error : new Error(String(error));
            setError(errorObj);
            setStatus('error');
            callbacks.onError?.(errorObj);
            callbacks.onStatusChange?.('error');
          },
          onMessage: (data) => {
            console.log('[VideoEngine] LiveKit Avatar message:', data);
            callbacks.onMessage?.(data);
          },
          onVideoReady: (videoElement) => {
            console.log(
              '[VideoEngine] LiveKit Avatar video ready - video attached by useLivekitAvatar',
            );
            // Video is now handled directly by useLivekitAvatar hook
          },
          onAgentStateChange: (state) => {
            console.log('[VideoEngine] LiveKit Agent state change:', state);

            // Map agent states to recording state
            if (state === 'listening') {
              setIsRecording(true);
            } else if (state === 'thinking' || state === 'speaking') {
              setIsRecording(false);
            }

            callbacks.onDebug?.({ agentState: state });
          },
        };

        await liveKitAvatar.startAvatarSession(avatarConfig, avatarCallbacks);

        console.log(
          '✅ LiveKit Avatar session started successfully:',
          newSessionId,
        );
        return newSessionId;
      } catch (error) {
        console.error('❌ LiveKit Avatar session start error:', error);
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        setError(errorObj);
        setStatus('error');
        throw errorObj;
      }
    },
    [liveKitAvatar, status, sessionId],
  );

  // Start HeyGen Session (renamed from original start function)
  const startHeyGen = useCallback(
    async (
      config: VideoEngineConfig,
      callbacks: VideoEngineCallbacks,
    ): Promise<string> => {
      // Prevent duplicate starts
      if (status === 'connecting' || status === 'connected') {
        console.log('[VideoEngine] HeyGen session already starting/started, skipping');
        return sessionId || 'existing-session';
      }

      if (!config.avatarId) {
        throw new Error('HeyGen requires avatarId');
      }

      console.log('[VideoEngine] Starting HeyGen session with config:', config);

      try {
        // Create unique session ID
        const newSessionId = `heygen-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 11)}`;
        setSessionId(newSessionId);

        // Get HeyGen API key
        const apiKey = import.meta.env.VITE_HEYGEN_API_TOKEN;
        if (!apiKey) {
          throw new Error('HeyGen API key not configured');
        }

        // Generate session token
        console.log('[VideoEngine] Requesting HeyGen session token');
        const sessionToken = await generateHeyGenSessionToken(apiKey);
        console.log('[VideoEngine] Received HeyGen session token');

        // Create StreamingAvatar instance
        console.log('[VideoEngine] Creating StreamingAvatar instance');
        const streamingAvatar = new StreamingAvatar({
          token: sessionToken,
        });

        streamingAvatarRef.current = streamingAvatar;

        // Set up HeyGen event handlers
        streamingAvatar.on(StreamingEvents.STREAM_READY, (event: any) => {
          console.log('[VideoEngine] HeyGen Stream ready:', event);

          setTimeout(() => {
            if (videoRef.current && event.detail) {
              let videoElement: HTMLVideoElement;

              // Check if htmlElement is provided directly
              if (event.detail.htmlElement) {
                videoElement = event.detail.htmlElement;
              }
              // Otherwise create one from the MediaStream
              else if (event.detail instanceof MediaStream) {
                videoElement = document.createElement('video');
                videoElement.srcObject = event.detail;
                videoElement.autoplay = true;
                videoElement.playsInline = true;
                videoElement.style.width = '100%';
                videoElement.style.height = '100%';
                videoElement.style.objectFit = 'cover';
                videoElement.style.backgroundColor = '#000';
              }
              // Also check for stream property
              else if (event.detail.stream) {
                videoElement = document.createElement('video');
                videoElement.srcObject = event.detail.stream;
                videoElement.autoplay = true;
                videoElement.playsInline = true;
                videoElement.style.width = '100%';
                videoElement.style.height = '100%';
                videoElement.style.objectFit = 'cover';
                videoElement.style.backgroundColor = '#000';
              }

              if (videoElement && videoRef.current) {
                // Clear any existing children safely
                while (videoRef.current.firstChild) {
                  videoRef.current.removeChild(videoRef.current.firstChild);
                }
                videoRef.current.appendChild(videoElement);
                videoElementRef.current = videoElement;
                console.log(
                  '[VideoEngine] HeyGen video element attached to container',
                );

                // Send first message after avatar is ready
                if (config.firstMessage) {
                  setTimeout(() => {
                    if (streamingAvatarRef.current) {
                      console.log(
                        '[VideoEngine] Speaking first message:',
                        config.firstMessage,
                      );
                      streamingAvatarRef.current.speak({
                        text: config.firstMessage,
                        taskType: TaskType.TALK,
                        taskMode: TaskMode.SYNC,
                      });
                    }
                  }, 1500); // Delay to ensure avatar is ready
                }
              } else {
                console.error(
                  '[VideoEngine] Could not create video element from stream data',
                );
              }
            }
          }, 200); // Small delay to ensure DOM is updated
        });

        streamingAvatar.on(StreamingEvents.AVATAR_START_TALKING, () => {
          console.log('[VideoEngine] HeyGen Avatar started talking');
          setIsSpeaking(true);
          isAvatarSpeakingRef.current = true;
          callbacks.onAgentSpeaking?.(true);
        });

        streamingAvatar.on(StreamingEvents.AVATAR_STOP_TALKING, () => {
          console.log('[VideoEngine] HeyGen Avatar stopped talking');
          setIsSpeaking(false);
          isAvatarSpeakingRef.current = false;
          callbacks.onAgentSpeaking?.(false);

          // Finalize avatar message after a short delay
          if (avatarMessageTimeoutRef.current) {
            clearTimeout(avatarMessageTimeoutRef.current);
          }

          avatarMessageTimeoutRef.current = setTimeout(() => {
            if (currentAvatarMessageRef.current.trim()) {
              callbacks.onMessage?.({
                source: 'ai',
                message: currentAvatarMessageRef.current.trim(),
              });
              currentAvatarMessageRef.current = '';
            }
          }, 300);
        });

        streamingAvatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
          console.log('[VideoEngine] HeyGen Stream disconnected');
          setStatus('disconnected');
          callbacks.onSessionEnd?.();
        });

        streamingAvatar.on(StreamingEvents.USER_START, () => {
          console.log('[VideoEngine] HeyGen User started speaking');
          setIsRecording(true);
          isUserSpeakingRef.current = true;
        });

        streamingAvatar.on(StreamingEvents.USER_STOP, () => {
          console.log('[VideoEngine] HeyGen User stopped speaking');
          setIsRecording(false);
          isUserSpeakingRef.current = false;

          // Finalize user message after a short delay
          if (userMessageTimeoutRef.current) {
            clearTimeout(userMessageTimeoutRef.current);
          }

          userMessageTimeoutRef.current = setTimeout(() => {
            if (currentUserMessageRef.current.trim()) {
              callbacks.onMessage?.({
                source: 'user',
                message: currentUserMessageRef.current.trim(),
              });
              currentUserMessageRef.current = '';
            }
          }, 300);
        });

        // Handle transcripts
        streamingAvatar.on(
          StreamingEvents.USER_TALKING_MESSAGE,
          (event: any) => {
            console.log('[VideoEngine] HeyGen User transcript:', event);
            if (event.detail?.message) {
              currentUserMessageRef.current += ' ' + event.detail.message;
              callbacks.onTranscript?.(event.detail.message, false);
            }
          },
        );

        streamingAvatar.on(
          StreamingEvents.AVATAR_TALKING_MESSAGE,
          (event: any) => {
            console.log('[VideoEngine] HeyGen Avatar transcript:', event);
            if (event.detail?.message) {
              currentAvatarMessageRef.current += ' ' + event.detail.message;
            }
          },
        );

        // Map quality settings
        const qualityMap = {
          low: AvatarQuality.Low,
          medium: AvatarQuality.Medium,
          high: AvatarQuality.High,
        };

        // Create session configuration with v2.1.0 improvements
        const sessionConfig = {
          avatarName: config.avatarId || 'June_HR_public',
          knowledgeBase: config.instructions || '', // Server will handle knowledge base
          voice: {
            // voiceId: config.voiceId,
            rate: 0.95,
          },
          language: 'en-US', // Server will handle language
          activityIdleTimeout: 600, // v2.1.0: Use activityIdleTimeout instead of disableIdleTimeout
          quality: qualityMap[config.quality || 'medium'], // Upgrade from 'low' to 'medium' for better stability
        };

        console.log(
          '[VideoEngine] Creating HeyGen streaming session with config:',
          sessionConfig,
        );
        await streamingAvatar.createStartAvatar(sessionConfig);

        // Start voice chat
        console.log('[VideoEngine] Starting HeyGen voice chat');
        await streamingAvatar.startVoiceChat({
          isInputAudioMuted: false,
        });

        setStatus('connected');
        callbacks.onConnect?.();
        callbacks.onStatusChange?.('connected');

        return newSessionId;
      } catch (err) {
        console.error('[VideoEngine] Error starting HeyGen session:', err);
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to start HeyGen session');
        setError(error);
        setStatus('error');
        callbacks.onError?.(error);
        callbacks.onStatusChange?.('error');
        throw error;
      }
    },
    [status, sessionId],
  );

  // Main Start function - routes to correct engine
  const start = useCallback(
    async (
      config: VideoEngineConfig,
      callbacks?: VideoEngineCallbacks,
      preferredEngineOverride?: VideoEngine,
    ): Promise<string> => {
      const selectedEngine = preferredEngineOverride || engine;
      console.log(`🚀 Starting ${selectedEngine} video engine`);

      // Store callbacks
      callbacksRef.current = callbacks || {};

      try {
        setStatus('connecting');
        setError(null);

        if (selectedEngine === 'livekit') {
          return await startLiveKit(config, callbacks || {});
        } else {
          return await startHeyGen(config, callbacks || {});
        }
      } catch (error) {
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        setStatus('error');
        setError(errorObj);
        callbacks?.onError?.(errorObj);
        throw errorObj;
      }
    },
    [engine, startLiveKit, startHeyGen],
  );

  // Stop function - updated to handle both engines
  const stop = useCallback(async () => {
    console.log(`🛑 Stopping ${engine} video engine`);
    await cleanup();
    callbacksRef.current.onSessionEnd?.();
  }, [engine, cleanup]);

  // Pause function - updated for both engines
  const pause = useCallback(() => {
    console.log(`[VideoEngine] Pausing ${engine} engine`);
    setPaused(true);
    // Note: LiveKit doesn't have native pause, HeyGen also limited
  }, [engine]);

  // Resume function - updated for both engines
  const resume = useCallback(() => {
    console.log(`[VideoEngine] Resuming ${engine} engine`);
    setPaused(false);
  }, [engine]);

  // Send message function - updated for both engines
  const sendMessage = useCallback(
    (message: string) => {
      if (
        engine === 'heygen' &&
        streamingAvatarRef.current &&
        status === 'connected'
      ) {
        console.log('[VideoEngine] Sending message to HeyGen:', message);
        streamingAvatarRef.current.speak({
          text: message,
          taskType: TaskType.TALK,
          taskMode: TaskMode.SYNC,
        });
      } else if (engine === 'livekit') {
        console.log(
          '[VideoEngine] Send message called for LiveKit (not implemented - avatar responds to voice):',
          message,
        );
        // LiveKit avatar handles conversation automatically through voice
      }
    },
    [engine, status],
  );

  // Cleanup on unmount only - no dependencies to prevent repeated cleanup calls
  useEffect(() => {
    return () => {
      console.log('[VideoEngine] Component unmounting, cleaning up');
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Core functions
    start,
    stop,
    pause,
    resume,
    sendMessage,

    // State - using LiveKit state when LiveKit engine is selected
    status:
      engine === 'livekit'
        ? liveKitAvatar.isConnected
          ? 'connected'
          : liveKitAvatar.isConnecting
          ? 'connecting'
          : liveKitAvatar.error
          ? 'error'
          : 'disconnected'
        : status,
    engine,
    isRecording:
      engine === 'livekit'
        ? isRecording // We manage this based on agent state
        : isRecording,
    isSpeaking:
      engine === 'livekit' ? liveKitAvatar.isAgentSpeaking : isSpeaking,
    isPaused,
    error:
      engine === 'livekit'
        ? liveKitAvatar.error
          ? new Error(liveKitAvatar.error)
          : null
        : error,
    sessionId,

    // UI Refs
    videoRef, // Your existing container that works for both engines
    userVideoRef,

    // Utilities
    getCurrentEngine: () => engine,
  };
}
